import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useCookieConsent, ConsentPreferences } from '@/hooks/useCookieConsent';
import { Cookie, Settings, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export const CookieConsent = () => {
  const { hasConsented, isLoading, acceptAll, rejectAll, savePreferences } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [localPrefs, setLocalPrefs] = useState<ConsentPreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    visitorId: false,
  });

  if (isLoading || hasConsented) {
    return null;
  }

  const handleSaveSettings = () => {
    savePreferences(localPrefs);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-2 sm:p-4 bg-background/95 backdrop-blur-sm border-t border-border shadow-lg">
      <div className="max-w-6xl mx-auto">
        {!showSettings ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center sm:items-start gap-2 sm:gap-3 flex-1">
              <Cookie className="h-5 w-5 sm:h-6 sm:w-6 text-primary shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground text-sm sm:text-base">Cookie Notice</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">
                  We use cookies to improve your experience. Residents of CA, VA, CO, CT, TX, and NV have additional rights.
                  <a href="/privacy#your-privacy-rights" className="text-primary hover:underline ml-1">Privacy Rights</a> • <a href="/do-not-sell" className="text-primary hover:underline">Do Not Sell</a>
                </p>
              </div>
            </div>
            <div className="flex gap-1.5 sm:gap-2 shrink-0 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="gap-1.5 h-8 text-xs sm:text-sm flex-1 sm:flex-none"
              >
                <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">Customize</span>
                <span className="xs:hidden">Settings</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={rejectAll}
                className="h-8 text-xs sm:text-sm flex-1 sm:flex-none"
              >
                Reject
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
                className="h-8 text-xs sm:text-sm flex-1 sm:flex-none"
              >
                Accept
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-foreground">Cookie Preferences</h3>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="p-1 hover:bg-muted rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="grid gap-3">
              <CookieCategory
                title="Essential Cookies"
                description="Required for the website to function. Cannot be disabled."
                checked={true}
                disabled={true}
              />
              <CookieCategory
                title="Analytics Cookies"
                description="Help us understand how visitors interact with our website."
                checked={localPrefs.analytics}
                onChange={(checked) => setLocalPrefs(p => ({ ...p, analytics: checked }))}
              />
              <CookieCategory
                title="Marketing Cookies"
                description="Used for targeted ads. May involve sharing data with advertising partners."
                checked={localPrefs.marketing}
                onChange={(checked) => setLocalPrefs(p => ({ ...p, marketing: checked }))}
              />
              <CookieCategory
                title="Visitor Identification"
                description="Identifies returning visitors via Salespanel (track_uid cookie, 1 year). Data processed for lead qualification only."
                checked={localPrefs.visitorId}
                onChange={(checked) => setLocalPrefs(p => ({ ...p, visitorId: checked }))}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="outline" size="sm" onClick={rejectAll}>
                Reject All
              </Button>
              <Button size="sm" onClick={handleSaveSettings}>
                Save Preferences
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface CookieCategoryProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}

const CookieCategory = ({ title, description, checked, disabled, onChange }: CookieCategoryProps) => (
  <div className={cn(
    "flex items-center justify-between p-3 rounded-lg border",
    disabled ? "bg-muted/50" : "bg-background"
  )}>
    <div className="flex-1 pr-4">
      <h4 className="font-medium text-sm text-foreground">{title}</h4>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
    <Switch
      checked={checked}
      onCheckedChange={onChange}
      disabled={disabled}
    />
  </div>
);

export default CookieConsent;
